import path from 'node:path';
import crypto from 'node:crypto';
import fs from 'node:fs';
import mime from 'mime';
import { useAsyncCallback } from '@wix/cli-ui-kit';
import { readFile } from '@wix/cli-fs';
import { CliError, CliErrorCode } from '@wix/cli-error';
import {
  useBackendAsAServiceClient,
  CloudProvider,
} from '@wix/backend-as-a-service-client';
import type {
  BackendFile,
  StaticFileMetadata,
} from '@wix/backend-as-a-service-client';
import { useProjectModel } from '../../../model.js';
import { readBuildMetadata } from '../../../files/build-metadata.js';

interface FileData {
  path: string;
  relativePath: string;
}

interface DeployFiles {
  client: FileData[];
  server: FileData[];
}

interface UploadBucket {
  hashes?: string[];
}

export function useDeployFiles() {
  const {
    model: { projectFolder, config },
  } = useProjectModel();

  const backendAsAServiceClient = useBackendAsAServiceClient();

  return useAsyncCallback(async (_, files: DeployFiles) => {
    const buildMetadata = await readBuildMetadata(projectFolder, config);
    const clientFilesMetadata = await Promise.all(
      files.client.map(getStaticFileMetadata)
    );

    const backendFiles = await Promise.all(
      files.server.map(getBackendFileData)
    );

    const hasClientFiles = clientFilesMetadata.length > 0;

    if (!hasClientFiles) {
      const { appDeployment } =
        await backendAsAServiceClient.createAppDeployment(
          config.appId,
          backendFiles,
          undefined,
          buildMetadata.generateAppDeploymentGroupId
        );

      if (!appDeployment?.id || !appDeployment.deploymentBaseUrl) {
        throw new CliError({
          code: CliErrorCode.FailedCreatingAppDeployment(),
          cause: null,
        });
      }

      return {
        deploymentId: appDeployment.id,
        deploymentBaseUrl: appDeployment.deploymentBaseUrl,
      };
    }

    const {
      appDeployment,
      staticFilesUploadUrls,
      uploadAuthToken,
      uploadBuckets,
    } = await backendAsAServiceClient.createAppDeployment(
      config.appId,
      undefined,
      clientFilesMetadata,
      buildMetadata.generateAppDeploymentGroupId
    );

    let completionAuthToken = uploadAuthToken;

    const clientDir = buildMetadata.clientDir;

    if (clientDir !== undefined && staticFilesUploadUrls?.[0]) {
      const isKubernetesCloudProvider =
        appDeployment?.cloudProviderOverride === CloudProvider.KUBERNETES;

      if (isKubernetesCloudProvider) {
        const uploadPromises = staticFilesUploadUrls.map(
          async (staticFileToUpload) => {
            const { uploadUrl, staticFileMetadata } = staticFileToUpload;

            const fileContent = await readFile(
              path.join(clientDir, staticFileMetadata.path)
            );

            return backendAsAServiceClient.putBinaryFile(
              uploadUrl,
              fileContent,
              staticFileMetadata.contentType
            );
          }
        );

        await Promise.all(uploadPromises);
      } else if (uploadAuthToken) {
        const uploadUrl = staticFilesUploadUrls[0].uploadUrl;
        const staticFilesMetadata = staticFilesUploadUrls.map(
          (item) => item.staticFileMetadata
        );

        const bucketsFormData = await buildBucketsFormData(
          clientDir,
          uploadBuckets ?? [],
          staticFilesMetadata
        );

        for (const formData of bucketsFormData) {
          const response = await backendAsAServiceClient.postForm(
            uploadUrl,
            uploadAuthToken,
            formData
          );

          const responseUploadToken = response.result?.jwt;

          if (responseUploadToken) {
            completionAuthToken = responseUploadToken;
          }
        }
      }
    }

    const completeAppDeployment =
      await backendAsAServiceClient.completeAppDeployment(
        {
          ...appDeployment,
          files: backendFiles,
        },
        completionAuthToken
      );

    return {
      deploymentId: completeAppDeployment.id,
      deploymentBaseUrl: completeAppDeployment.deploymentBaseUrl,
    };
  });
}

async function getStaticFileMetadata(
  file: FileData
): Promise<StaticFileMetadata> {
  const stats = await fs.promises.stat(file.path);
  const fileContent = await readFile(file.path);
  const hash = crypto.createHash('md5').update(fileContent).digest('hex');

  return {
    size: stats.size,
    hash,
    contentType: mime.getType(file.path) ?? 'application/octet-stream',
    path: `/${file.relativePath}`,
  };
}

async function getBackendFileData(file: FileData): Promise<BackendFile> {
  const buffer = await readFile(file.path);
  return { path: file.relativePath, content: buffer.toString('base64') };
}

async function buildBucketsFormData(
  clientDir: string,
  uploadBuckets: UploadBucket[],
  staticFilesMetadata: StaticFileMetadata[]
): Promise<FormData[]> {
  if (uploadBuckets.length === 0) {
    return [];
  }

  const staticFilesMetadataByHash = new Map(
    staticFilesMetadata.map((metadata) => [metadata.hash, metadata])
  );

  const batches: FormData[] = [];

  for (const uploadBucket of uploadBuckets) {
    if (!uploadBucket.hashes || uploadBucket.hashes.length === 0) {
      continue;
    }

    const form = new FormData();

    await Promise.all(
      uploadBucket.hashes.map(async (hash) => {
        const staticFileMetadata = staticFilesMetadataByHash.get(hash);
        if (!staticFileMetadata) {
          return;
        }

        const fileContents = await readFile(
          path.join(clientDir, staticFileMetadata.path)
        );

        form.append(
          hash,
          new File([fileContents.toString('base64')], hash, {
            type: staticFileMetadata.contentType,
          }),
          hash
        );
      })
    );

    batches.push(form);
  }

  return batches;
}
